import { apiClient } from '../apiClient';

// The review cycle. The org dashboard could always show what a student's
// readiness IS; it could never show what it DID. These types are the shape of
// "what changed since we last looked" — the gap a college named as their
// biggest concern with the platform.

/** What happened to one student between two review periods. */
export type ReviewMovement = 'improved' | 'slipped' | 'flat' | 'new' | 'no_data';

/**
 * The thresholds the server applied, published alongside the result so the UI
 * can explain why a row is listed without duplicating the numbers. A hardcoded
 * "moved more than 5 points" in TSX is exactly how the v1 readiness copy
 * drifted from the scorer.
 */
export interface ReviewThresholds {
  score_delta: number;
  min_coverage: number;
  stale_days: number;
}

export interface StudentMovementRow {
  user_id: string;
  name: string;
  email?: string;
  score: number;
  tier?: string;
  /** Absent for a student with no prior period — NOT a change from zero. */
  previous_score?: number;
  previous_tier?: string;
  delta?: number;
  movement: ReviewMovement;
  tier_changed: boolean;
  /** Crossed into the bottom tier this period. The first thing to act on. */
  newly_at_risk: boolean;
  data_coverage: number;
  /** The movement was suppressed as untrustworthy: too little data behind it. */
  low_confidence: boolean;
  days_since_activity?: number;
  stalled: boolean;
}

/** Fixed-size distribution for a period — readable at any batch size. */
export interface ReviewCounts {
  total: number;
  improved: number;
  slipped: number;
  flat: number;
  new: number;
  no_data: number;
  newly_at_risk: number;
  stalled: number;
  low_confidence: number;
}

export interface BatchReview {
  batch_id: string;
  batch_name: string;
  period_start: string;
  /** Absent in the first period, when there is nothing to compare against. */
  previous_period_start?: string;
  /**
   * Fewer than two periods on record. The UI must say "collecting" — an empty
   * movement list otherwise reads as "nothing changed", which is a different
   * and much more reassuring claim than the truth.
   */
  collecting: boolean;
  counts: ReviewCounts;
  movements: StudentMovementRow[];
  thresholds: ReviewThresholds;
}

/** One period on a student's readiness trend. */
export interface ReadinessTrendPoint {
  period_start: string;
  score: number;
  tier?: string;
  /** False when nothing was measured yet — render a gap, never a zero. */
  has_data: boolean;
  data_coverage: number;
  formula_version: number;
  /** Reconstructed from past facts rather than captured at the time. */
  backfilled: boolean;
}

/** The three words the college asked for, plus the series behind them. */
export type TrendDirection = 'improving' | 'flat' | 'slipping' | 'unknown';

export interface ReadinessTrend {
  points: ReadinessTrendPoint[];
  direction: TrendDirection;
  /** First-to-last change across measured points only. */
  change?: number;
  /**
   * The series spans a formula change, so part of its slope belongs to the
   * formula rather than to the student. Surfaced rather than smoothed over.
   */
  mixed_formula_versions: boolean;
}

export const reviewApi = {
  getBatchReview: async (batchId: string): Promise<BatchReview> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/review`);
    return data as BatchReview;
  },
  getStudentReadinessHistory: async (
    batchId: string,
    userId: string,
  ): Promise<ReadinessTrend> => {
    const { data } = await apiClient.get(
      `/api/v1/org/batches/${batchId}/students/${userId}/readiness-history`,
    );
    return data as ReadinessTrend;
  },
};

/** Human label for a movement, matching the server's semantics exactly. */
export const MOVEMENT_LABEL: Record<ReviewMovement, string> = {
  slipped: 'Slipped',
  improved: 'Improved',
  flat: 'No change',
  new: 'New this period',
  no_data: 'Not enough data',
};

/**
 * Signed change as display text. Returns undefined when there is no previous
 * period, so callers render "new" rather than a fabricated delta.
 */
export function formatDelta(delta: number | undefined): string | undefined {
  if (delta === undefined) return undefined;
  if (delta === 0) return '±0';
  return delta > 0 ? `+${delta}` : `${delta}`;
}
